import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ChevronUp, ChevronDown, Eye, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cmsCall } from "@/lib/cms";
import { DEFAULT_PDP_BLOKKEN, PDP_BLOK_TYPES, STANDAARD_GEGEVENS, mobielBasis } from "@/lib/pdpTemplate";
import { clearPdpTemplateCache } from "@/hooks/usePdpTemplates";
import { MobielIndeling, VoorbeeldDialog, WeergaveTabs } from "@/pages/beheer/WeergaveIndeling";

const KOLOMMEN = [
  { waarde: "links", label: "Links (media/tekst)" },
  { waarde: "rechts", label: "Rechts (koopkolom, sticky)" },
  { waarde: "onder", label: "Onder de kolommen (volle breedte)" },
];

const nieuwBlok = (type) => {
  const meta = PDP_BLOK_TYPES.find((b) => b.type === type);
  return {
    id: `${type}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    kolom: meta?.kolom || "rechts",
    velden: type === "velden" ? ["sku", "ean", "eenheid"] : type === "tabs" ? [...STANDAARD_GEGEVENS] : [],
    tekst: {},
    titel: {},
    marge_badge: true,
  };
};

const BlokKaart = ({ blok, index, aantal, velden, TextField, onChange, onMove, onDelete }) => {
  const meta = PDP_BLOK_TYPES.find((b) => b.type === blok.type);
  const toggleVeld = (key) => {
    const huidig = blok.velden || [];
    onChange({ ...blok, velden: huidig.includes(key) ? huidig.filter((v) => v !== key) : [...huidig, key] });
  };

  return (
    <Card className="p-4" data-testid={`pdp-blok-${blok.type}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{meta?.label || blok.type}</p>
          <p className="text-xs text-muted-foreground">Blok {index + 1} van {aantal}</p>
        </div>
        <div className="flex items-center gap-1">
          <select
            value={blok.kolom || "rechts"}
            onChange={(e) => onChange({ ...blok, kolom: e.target.value })}
            className="mr-2 h-9 rounded-md border bg-background px-2 text-sm"
            data-testid={`pdp-blok-kolom-${blok.type}`}
          >
            {KOLOMMEN.map((k) => <option key={k.waarde} value={k.waarde}>{k.label}</option>)}
          </select>
          <Button variant="ghost" size="icon" onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`pdp-blok-up-${index}`}>
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onMove(index, 1)} disabled={index === aantal - 1} data-testid={`pdp-blok-down-${index}`}>
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(index)} data-testid={`pdp-blok-delete-${index}`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {blok.type === "gallery" && (
        <label className="mt-3 flex items-center gap-2 text-sm">
          <Checkbox
            checked={blok.marge_badge !== false}
            onCheckedChange={(v) => onChange({ ...blok, marge_badge: Boolean(v) })}
            data-testid="pdp-blok-marge-badge"
          />
          Margebadge over de foto tonen
        </label>
      )}

      {(blok.type === "velden" || blok.type === "tabs") && (
        <div className="mt-3">
          <Label className="text-xs text-muted-foreground">{blok.type === "tabs" ? "CRM-velden in de tab Productgegevens (lege velden worden verborgen)" : "CRM-velden in de tabel"}</Label>
          <div className="mt-2 grid gap-1 sm:grid-cols-2">
            {velden.map((veld) => (
              <label key={veld.key} className="flex items-center gap-2 rounded-md px-1 py-1 text-sm">
                <Checkbox
                  checked={(blok.velden || []).includes(veld.key)}
                  onCheckedChange={() => toggleVeld(veld.key)}
                  data-testid={`pdp-veld-${veld.key}`}
                />
                <span className="flex-1">{veld.label}</span>
                {veld.geleverd === false && (
                  <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-800">CRM levert nog niet</span>
                )}
                {veld.geleverd === true && (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">live</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {blok.type === "tekst" && (
        <div className="mt-3 space-y-3">
          <TextField label="Kop (optioneel)" value={blok.titel || {}} onChange={(titel) => onChange({ ...blok, titel })} />
          <TextField label="Tekst" value={blok.tekst || {}} onChange={(tekst) => onChange({ ...blok, tekst })} rows={4} />
          <p className="text-xs text-muted-foreground">
            Gebruik CRM-velden met dubbele accolades, bijvoorbeeld: Dit product wordt geleverd per{" "}
            <code className="font-mono">{"{{besteleenheid}}"}</code>. Beschikbaar:{" "}
            {velden.map((v) => <code key={v.key} className="mr-1 font-mono text-[11px]">{`{{${v.key}}}`}</code>)}
          </p>
        </div>
      )}
    </Card>
  );
};

const ProducttekstStatus = () => {
  const [data, setData] = useState(null);
  const [taal, setTaal] = useState("nl");

  useEffect(() => {
    cmsCall("GET", "/beheer/producttekst-status", null, { admin: true })
      .then(setData)
      .catch((e) => toast.error(e.message));
  }, []);

  if (!data) return null;
  const lijst = data.ontbreekt?.[taal] || [];

  return (
    <Card className="p-5" data-testid="beheer-producttekst-status">
      <h2 className="font-display text-lg font-semibold">Producten zonder productteksten</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        De omschrijving op de productpagina komt uit de productteksten per taal in het CRM. Staat er voor een taal
        geen tekst, dan blijft de omschrijving leeg. {data.melding}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {(data.talen || []).map((code) => (
          <Button
            key={code}
            size="sm"
            variant={taal === code ? "default" : "outline"}
            onClick={() => setTaal(code)}
            data-testid={`producttekst-taal-${code}`}
          >
            {code.toUpperCase()} · {(data.ontbreekt?.[code] || []).length}/{data.totaal}
          </Button>
        ))}
      </div>

      {lijst.length === 0 ? (
        <p className="mt-4 text-sm" data-testid="producttekst-compleet">
          Alle {data.totaal} producten hebben een tekst in {taal.toUpperCase()}.
        </p>
      ) : (
        <div className="mt-4 max-h-72 overflow-auto rounded-md border" data-testid="producttekst-lijst">
          <table className="w-full text-left text-sm">
            <tbody>
              {lijst.map((p) => (
                <tr key={p.id || p.sku} className="border-b last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
                  <td className="px-3 py-2">{p.naam}</td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                    {p.naam_ontbreekt ? "naam + omschrijving" : "omschrijving"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const AfbeeldingStatus = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    cmsCall("GET", "/beheer/afbeelding-status", null, { admin: true })
      .then(setData)
      .catch((e) => toast.error(e.message));
  }, []);

  if (!data) return null;
  const lijst = data.zonder || [];

  return (
    <Card className="p-5" data-testid="beheer-afbeelding-status">
      <h2 className="font-display text-lg font-semibold">Producten zonder afbeelding</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        De webshop toont alleen foto's die het CRM meestuurt in <code className="font-mono text-xs">hoofdfoto</code> of{" "}
        <code className="font-mono text-xs">afbeeldingen</code>. Bij dochter- of variantproducten moet het CRM de foto
        van het hoofdproduct meesturen; die overerving gebeurt in het CRM, de webshop kan het hoofdproduct niet zelf
        opzoeken. {data.melding}
      </p>

      {lijst.length === 0 ? (
        <p className="mt-4 text-sm" data-testid="afbeelding-compleet">
          Alle {data.totaal} producten hebben een afbeelding.
        </p>
      ) : (
        <>
          <p className="mt-4 text-sm font-medium" data-testid="afbeelding-aantal">
            {lijst.length} van {data.totaal} producten zonder afbeelding
          </p>
          <div className="mt-2 max-h-72 overflow-auto rounded-md border" data-testid="afbeelding-lijst">
            <table className="w-full text-left text-sm">
              <tbody>
                {lijst.map((p) => (
                  <tr key={p.id || p.sku} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
                    <td className="px-3 py-2">{p.naam}</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{p.categorie || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};

export const Productpagina = ({ TextField }) => {
  const [data, setData] = useState(null);
  const [categorie, setCategorie] = useState("");
  const [blokken, setBlokken] = useState([]);
  const [mobiel, setMobiel] = useState(null);
  const [basis, setBasis] = useState("");
  const [weergave, setWeergave] = useState("desktop");
  const [voorbeeld, setVoorbeeld] = useState(false);
  const [voorbeeldPad, setVoorbeeldPad] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nieuwType, setNieuwType] = useState(PDP_BLOK_TYPES[0].type);

  const laad = useCallback(() => {
    cmsCall("GET", "/beheer/pdp-templates", null, { admin: true })
      .then(setData)
      .catch((e) => toast.error(e.message));
  }, []);
  useEffect(laad, [laad]);
  useEffect(() => {
    cmsCall("GET", "/content/schap")
      .then((d) => d?.producten?.[0]?.id && setVoorbeeldPad(`/producten/${d.producten[0].id}`))
      .catch(() => {});
  }, []);

  const categorieen = useMemo(() => Object.keys(data?.per_categorie || {}), [data]);

  useEffect(() => {
    if (!data) return;
    const template = categorie ? data.per_categorie?.[categorie] : data.standaard;
    const b = template?.blokken?.length ? template.blokken : DEFAULT_PDP_BLOKKEN;
    const m = template?.mobiel || null;
    setBlokken(b);
    setMobiel(m);
    setBasis(JSON.stringify({ b, m }));
  }, [data, categorie]);

  const gewijzigd = basis !== "" && JSON.stringify({ b: blokken, m: mobiel }) !== basis;

  const wijzigBlok = (index, blok) => setBlokken((prev) => prev.map((b, i) => (i === index ? blok : b)));
  const verplaats = (index, richting) =>
    setBlokken((prev) => {
      const next = [...prev];
      const doel = index + richting;
      if (doel < 0 || doel >= next.length) return prev;
      [next[index], next[doel]] = [next[doel], next[index]];
      return next;
    });
  const verwijder = (index) => setBlokken((prev) => prev.filter((_, i) => i !== index));

  const opslaan = async () => {
    setBusy(true);
    try {
      await cmsCall("PUT", "/beheer/pdp-template", { categorie: categorie || null, blokken, mobiel }, { admin: true });
      clearPdpTemplateCache();
      toast.success("Productpagina opgeslagen");
      laad();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const categorieVerwijderen = async () => {
    if (!categorie) return;
    setBusy(true);
    try {
      await cmsCall("DELETE", `/beheer/pdp-template?categorie=${encodeURIComponent(categorie)}`, null, { admin: true });
      clearPdpTemplateCache();
      toast.success("Categorie-template verwijderd; de standaard geldt weer");
      setCategorie("");
      laad();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;

  const labelVan = (b) => PDP_BLOK_TYPES.find((x) => x.type === b.type)?.label || b.type;

  return (
    <div className="space-y-6" data-testid="beheer-productpagina">
      <div className="sticky top-0 z-20 -mx-5 -mt-5 border-b bg-background/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:-mt-8 sm:px-8" data-testid="pdp-werkbalk">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <WeergaveTabs value={weergave} onChange={setWeergave} testIdPrefix="pdp-weergave" />
            <Button variant="outline" size="sm" onClick={() => setVoorbeeld(true)} data-testid="pdp-preview-link">
              <Eye className="mr-2 h-4 w-4" />Bekijk voorbeeld
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {gewijzigd && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700" data-testid="pdp-niet-opgeslagen">
                <span className="h-2 w-2 rounded-full bg-amber-500" />Niet-opgeslagen wijzigingen
              </span>
            )}
            {categorie && (
              <Button variant="ghost" size="sm" onClick={categorieVerwijderen} disabled={busy} data-testid="pdp-categorie-verwijderen">
                <Trash2 className="mr-2 h-4 w-4" />Categorie-template verwijderen
              </Button>
            )}
            <Button onClick={opslaan} disabled={busy} data-testid="pdp-opslaan">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Opslaan
            </Button>
          </div>
        </div>
      </div>

      <Card className="p-5">
        <h2 className="font-display text-lg font-semibold">Productpagina bouwen</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Stel zelf samen wat er op de productpagina staat en in welke kolom. Alle productdata komt live uit het CRM;
          het beheer bewaart alleen jouw indeling. Vergeet niet op <strong>Opslaan</strong> te klikken.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="pdp-template-keuze" className="text-xs text-muted-foreground">Template</Label>
            <select
              id="pdp-template-keuze"
              value={categorie}
              onChange={(e) => setCategorie(e.target.value)}
              className="mt-1 h-10 rounded-md border bg-background px-3 text-sm"
              data-testid="pdp-template-select"
            >
              <option value="">Standaard (alle producten)</option>
              {categorieen.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="pdp-nieuwe-categorie" className="text-xs text-muted-foreground">Nieuwe categorie-template</Label>
            <input
              id="pdp-nieuwe-categorie"
              placeholder="bijv. Interdentale mondzorg"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.currentTarget.value.trim()) {
                  setCategorie(e.currentTarget.value.trim());
                  e.currentTarget.value = "";
                }
              }}
              className="mt-1 h-10 w-64 rounded-md border bg-background px-3 text-sm"
              data-testid="pdp-nieuwe-categorie-input"
            />
          </div>
        </div>

        {categorie && (
          <Alert className="mt-4">
            <AlertDescription>
              Je bewerkt de template voor <strong>{categorie}</strong>. Producten in deze categorie gebruiken deze
              indeling; alle andere producten volgen de standaard.
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {weergave === "mobiel" ? (
        <MobielIndeling blokken={mobielBasis(blokken)} mobiel={mobiel} labelVan={labelVan} onChange={setMobiel} testIdPrefix="pdp-mobiel" />
      ) : (
        <>
          <div className="space-y-3">
            {blokken.map((blok, index) => (
              <BlokKaart
                key={blok.id || `${blok.type}-${index}`}
                blok={blok}
                index={index}
                aantal={blokken.length}
                velden={data.velden || []}
                TextField={TextField}
                onChange={(next) => wijzigBlok(index, next)}
                onMove={verplaats}
                onDelete={verwijder}
              />
            ))}
          </div>

          <Card className="p-5">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label htmlFor="pdp-nieuw-blok" className="text-xs text-muted-foreground">Blok toevoegen</Label>
                <select
                  id="pdp-nieuw-blok"
                  value={nieuwType}
                  onChange={(e) => setNieuwType(e.target.value)}
                  className="mt-1 h-10 rounded-md border bg-background px-3 text-sm"
                  data-testid="pdp-nieuw-blok-select"
                >
                  {PDP_BLOK_TYPES.map((b) => <option key={b.type} value={b.type}>{b.label}</option>)}
                </select>
              </div>
              <Button variant="outline" onClick={() => setBlokken((prev) => [...prev, nieuwBlok(nieuwType)])} data-testid="pdp-blok-toevoegen">
                <Plus className="mr-2 h-4 w-4" />Toevoegen
              </Button>
              <Button variant="ghost" onClick={() => { setBlokken(DEFAULT_PDP_BLOKKEN); setMobiel(null); }} data-testid="pdp-reset">
                <RotateCcw className="mr-2 h-4 w-4" />Terug naar standaardindeling
              </Button>
            </div>
          </Card>
        </>
      )}

      <VoorbeeldDialog
        open={voorbeeld}
        onOpenChange={setVoorbeeld}
        pad={voorbeeldPad}
        titel="Voorbeeld productpagina"
        melding="Toont de opgeslagen indeling op een echt product. Productpagina's zijn alleen zichtbaar als je in deze browser ook als klant bent ingelogd."
      />

      <ProducttekstStatus />
      <AfbeeldingStatus />
    </div>
  );
};
